/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Button,
  Loading,
  ProgressIndicator,
  ProgressStep
} from 'carbon-components-react';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { Noop, ApiStatus } from '../../../../ts';
import { FeatureFlag } from '../../../../ts/feature-flags';
import {
  ELASTIC_SEARCH_INDEX_INPUT_METHODS,
  ELASTIC_SEARCH_AUTH_TYPES,
  SEARCH_INTEGRATION_TYPES
} from '../../../../ts/skill';
import { ReadOnlyNotification } from '../../../ia/components/access-control/ReadOnlyNotification';
import { EnvironmentTag } from '../../../ia/components/environment-tag/EnvironmentTag';
import Panel from '../../../ia/components/panel/Panel';
import { isSkillReadOnly } from '../../../ia/store/access-control/selectors';
import { selectAssistant } from '../../../ia/store/assistants/core-selectors';
import { updateEnvironment } from '../../../ia/store/environments/actions';
import {
  selectIsSkillEnabledInEnvironment,
  selectSkillReferencesForEnvironment,
  selectIsUpdatingEnvironment
} from '../../../ia/store/environments/selectors';
import {
  isFeatureEnabled,
  selectIsConversationalSearchEnabled,
  selectIsInsightActionsEnabled
} from '../../../ia/store/features/selectors';
import { listSkills, updateSkill } from '../../../ia/store/skills/actions';
import { selectSkillById } from '../../../ia/store/skills/core-selectors';
import {
  selectIsUpdatingSkill,
  selectIsCreatingSkill,
  selectAreSkillsLoading
} from '../../../ia/store/skills/selectors';
import { useElasticDraft } from '../../hooks/useElasticDraft';
import { getElserSupport, listDocs, createElser } from '../../store/actions';
import {
  clearDiscoveryDetails,
  clearElasticApiKey,
  clearElasticPass,
  clearElasticUser,
  resetIsDirty,
  resetElasticState,
  setElasticAuthTypeDefault,
  setElasticSearchSkillDraft,
  setPreviouslySavedElasticAuthType,
  setShowDetailConfig,
  setIsElasticSetupValid,
  clearContentMapping
} from '../../store/reducer';
import {
  isShowDetailConfig,
  selectDraftSearchSkill,
  selectElasticAuthType,
  selectElasticIndexInputMethod,
  selectDraftElasticIndex,
  selectIsCreateElserLoading,
  selectIsElasticSetupValid,
  selectDraftElasticUser,
  selectDraftElasticPass,
  selectDraftElasticUrl,
  selectDraftElasticApiKey,
  selectIsElasticConfigValid,
  selectIsDocumentUploadEnabled,
  selectIsElserSupportedLoading,
  selectIsElserSupportedStatus
} from '../../store/selectors';
import { ConversationalSearch } from '../ConversationalSearch';
import { ElasticSearchInstanceConfig } from '../elastic-search-instance-config/ElasticSearchInstanceConfig';
import { ElasticSearchIndexConfig } from '../ElasticSearchIndexConfig';
import { ElasticSearchTabs } from '../ElasticSearchTabs';
import { messages } from './messages';
import './ElasticSearchOverlay.scss';

interface ElasticSearchOverlayProps {
  environmentId: string;
  skillId: string;
  onClose: Noop;
}

const ElasticSearchOverlay = ({
  environmentId,
  skillId,
  onClose
}: ElasticSearchOverlayProps) => {
  const formatMsg = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const draft = useElasticDraft();
  const isUpdating = useAppSelector(selectIsUpdatingSkill);
  const isEnvironmentUpdating = useAppSelector(selectIsUpdatingEnvironment);
  const isCreating = useAppSelector(selectIsCreatingSkill);
  const showDetailConfig = useAppSelector(isShowDetailConfig);
  const skill = useAppSelector(state => selectSkillById(state, skillId));
  const elasticState = skill?.search_settings?.elastic_search;
  const draftSkill = useAppSelector(selectDraftSearchSkill);
  const areSkillsLoading = useAppSelector(selectAreSkillsLoading);
  const assistant = useAppSelector(selectAssistant);
  const assistantId = assistant?.assistant_id;
  const elasticInputMethod = useAppSelector(selectElasticIndexInputMethod);
  const indexSelected = useAppSelector(selectDraftElasticIndex);
  const isElasticSetupValid = useAppSelector(selectIsElasticSetupValid);
  const isElasticConfigValid = useAppSelector(selectIsElasticConfigValid);
  const createElserLoading = useAppSelector(selectIsCreateElserLoading);
  const elserSupportedLoading = useAppSelector(selectIsElserSupportedLoading);
  const elserSupportedStatus = useAppSelector(selectIsElserSupportedStatus);

  const draftUser = useAppSelector(selectDraftElasticUser);
  const draftPass = useAppSelector(selectDraftElasticPass);
  const draftUrl = useAppSelector(selectDraftElasticUrl);
  const draftApiKey = useAppSelector(selectDraftElasticApiKey);
  const [progressIndex, setProgressIndex] = useState(0);
  const isEnabled = useAppSelector(state =>
    selectIsSkillEnabledInEnvironment(state, environmentId, skillId)
  );
  const isDocumentUploadEnabled = useAppSelector(selectIsDocumentUploadEnabled);

  const elasticAuthType = useAppSelector(selectElasticAuthType);
  const [isInitializingDraftSkill, setIsInitializingDraftSkill] =
    useState(true);
  const skillReferences = useAppSelector(state =>
    selectSkillReferencesForEnvironment(state, environmentId)
  );

  const isSapConversationalSearchEnabled = useAppSelector(state =>
    isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH)
  );

  const isInsightActionsEnabled = useAppSelector(selectIsInsightActionsEnabled);
  const conversationalSearchToggleVisible = useAppSelector(
    selectIsConversationalSearchEnabled
  );

  useEffect(() => {
    if (skillId && !skill) {
      dispatch(listSkills(environmentId));
    }
  }, [skillId, skill, dispatch, environmentId]);

  useEffect(
    () => {
      // Set the right details in the search skill draft depending on if elastic_search details already exist on the search
      // skill or not.
      if (skill?.search_settings?.elastic_search) {
        const elasticState = skill?.search_settings?.elastic_search;
        if (
          isInsightActionsEnabled &&
          skill?.search_settings?.conversational_search?.search_confidence
            ?.threshold
        ) {
          delete skill.search_settings.conversational_search.search_confidence
            .threshold;
        }
        // If a search skill definition that includes search_settings.elastic_search fields exists then ES has already
        // been configured once, so set its details into the editing draft.
        dispatch(setElasticSearchSkillDraft({ skill, isDirty: false }));

        //If index, username and url exist configuration is setup go to tab screen

        if (elasticState?.url && elasticState?.index) {
          if (
            isDocumentUploadEnabled &&
            elasticState.managed_index === elasticState.index
          ) {
            dispatch(listDocs({ skillId, assistantId, noLoad: false }));
          }
          dispatch(setIsElasticSetupValid(true));
          dispatch(getElserSupport({ skillId, assistantId }));
        } else {
          dispatch(setIsElasticSetupValid(false));
        }
        // The starting auth type is basic if a username already exists. Otherwise if no username exists then the
        // starting type must be API.
        const startingAuthType = skill.search_settings?.elastic_search?.username
          ? ELASTIC_SEARCH_AUTH_TYPES.BASIC
          : ELASTIC_SEARCH_AUTH_TYPES.API;
        dispatch(setPreviouslySavedElasticAuthType(startingAuthType));
      } else if (
        skill &&
        skill?.search_settings &&
        (draft.isUninitialized || skill?.skill_id !== draft?.skillId)
      ) {
        // If the search_settings.elastic_search fields are missing then this was either previously a Discovery search
        // skill or this is a new-never-configured skill. Either way, if the draft hasn't been initialized or the draft
        // skillID doesn't match the search skill skill_id, then use initializeAutocreatedSkill to make a new editing
        // draft and copy the few details from the existing search skill (if not previously a Discovery search skill
        // then it will be a near-empty auto created search skill), and initialize other fields.
        draft.initializeAutocreatedElasticSkill(skill);

        // Since we're starting from scratch, set the default auth type to basic and don't set a previously saved auth
        // type value.
        dispatch(setElasticAuthTypeDefault());
      }

      // If this was previously a Discovery search skill make sure to delete those settings from the draft.
      if (skill?.search_settings?.discovery) {
        dispatch(clearDiscoveryDetails());
      }

      if (conversationalSearchToggleVisible) {
        if (_.isEmpty(skill?.search_settings?.conversational_search)) {
          // If conversational search is enabled, and this is an older skill that doesn't have anything set for
          // conversational_search, make sure to set the values in draft to the defaults.
          draft.setConversationalSearchEnabledDefault();
          if (isInsightActionsEnabled) {
            draft.setConversationalSearchRetrievalConfidenceThresholdDefault();
            draft.setConversationalSearchResponseConfidenceThresholdDefault();
          } else {
            draft.setConversationalSearchConfidenceThresholdDefault();
          }
          draft.setConversationalSearchVerbosityOptionDefault();
          draft.setConversationalSearchMultiTurnDefault();
          if (!isSapConversationalSearchEnabled) {
            // If SAP conversational search is enabled we don't want to set a citations_title because they are using a
            // text response type instead of the new conversational_search response type. If SAP conversational search
            // is disabled then set a default for citations_title.
            draft.setCitationsTitleValueDefault();
          }
        } else if (
          !skill?.search_settings?.conversational_search?.citations_title &&
          !isSapConversationalSearchEnabled
        ) {
          // If this is a newer skill that includes conversational_search settings, but there is no value for the
          // citations title in the skill, and SAP conversational search is disabled, then make sure to set the value in
          // draft to the default.
          draft.setCitationsTitleValueDefault();
        }
      }

      // After the draft skill is done being initialized update state to close the loading spinner.
      setIsInitializingDraftSkill(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including all `draft` causes loop, need only isUninitialized & skillId
    [
      dispatch,
      draft.isUninitialized,
      draft?.skillId,
      conversationalSearchToggleVisible,
      isSapConversationalSearchEnabled,
      skill
    ]
  );

  // Update mapping when index changes.
  useEffect(() => {
    if (indexSelected !== '') {
      if (skill?.search_settings?.elastic_search) {
        const elasticState = skill?.search_settings?.elastic_search;

        if (elasticState?.url) {
          if (
            indexSelected === elasticState?.managed_index ||
            elasticInputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.INDEX
          ) {
            // dispatch(getIndexMapping(skillId));
          } else if (
            indexSelected !== elasticState?.managed_index &&
            elasticInputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD
          ) {
            // Upload index not create clear the content mapping
            dispatch(clearContentMapping());
          }
        }
      }
    }
  }, [
    dispatch,
    indexSelected,
    elasticInputMethod,
    skillId,
    skill?.search_settings?.elastic_search
  ]);

  useEffect(() => {
    if (elserSupportedStatus === ApiStatus.SUCCEEDED) {
      // dispatch(getElserSupport({ skillId, assistantId }));

      // Before moving to the next panel we need to remove any unnecessary auth items from the elastic settings since
      // store will only except one of these sets of values.
      if (elasticAuthType === ELASTIC_SEARCH_AUTH_TYPES.API) {
        dispatch(clearElasticUser());
        dispatch(clearElasticPass());
      } else if (elasticAuthType === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
        dispatch(clearElasticApiKey());
      }

      // If the instance config is valid then the submit button will be enabled. On submit click we need to move to
      // the detail config panel.
      dispatch(setShowDetailConfig(true));
      if (!isElasticSetupValid) {
        setProgressIndex(progressIndex + 1);
      }
    }
  }, [dispatch, elasticAuthType, elserSupportedStatus]);

  // Clean up elastic state when leaving without closing the panel.
  useEffect(() => {
    return () => {
      dispatch(resetElasticState());
    };
  }, []);

  const handleClose = useCallback(() => {
    dispatch(resetElasticState());
    onClose();
  }, [dispatch, onClose]);

  const updateSkillAndEnvironment = async () => {
    // Send the updated skill to store.

    // This will set the managed index when the doc index is selected.
    // It will also reset the managed index when isElasticSetupValid is not valid. Used to reset the value when URL credentials change.
    if (elasticInputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD) {
      draftSkill.search_settings.elastic_search.managed_index = indexSelected;
    } else if (!isElasticSetupValid) {
      draftSkill.search_settings.elastic_search.managed_index = '';
    }
    if (
      isInsightActionsEnabled &&
      draftSkill?.search_settings?.conversational_search?.search_confidence
        ?.threshold
    ) {
      delete draftSkill.search_settings.conversational_search.search_confidence
        .threshold;
    }

    await dispatch(updateSkill(draftSkill)).unwrap();

    // If the skill has not been enabled yet (because it was a pre-created skill and this is the first time it's
    // being setup) then send another post to set the skill_ref to enabled.
    if (!isEnabled) {
      const updatedSkillRefs = _.cloneDeep(skillReferences);
      const targetSkill = updatedSkillRefs.find(
        skillRef => skillRef.skill_id === skillId
      );
      targetSkill.disabled = false;
      await dispatch(
        updateEnvironment({
          assistant_id: assistantId,
          environment_id: environmentId,
          skill_references: updatedSkillRefs
        })
      );
    }

    // Reset progress index
    setProgressIndex(0);
    // Now that the skill has been updated, reset the isDirty property on the elastic draft.
    await dispatch(resetIsDirty());
  };
  const finalizeInitialConfig = async () => {
    if (elasticInputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD) {
      let createIndexResponse = null;

      // Send API request to create upload index only if one doesn't exist.
      createIndexResponse = await dispatch(
        createElser({ skillId, assistantId, index: indexSelected })
      );

      // We should stop the update if creating the elser index fails.
      if (createIndexResponse.payload?.index) {
        // Update the skill with the settings
        await updateSkillAndEnvironment();
      }
    } else {
      // Update the skill with the settings
      await updateSkillAndEnvironment();
    }
  };

  const handleSubmit = useCallback(async () => {
    try {
      if (progressIndex === 1) {
        if (conversationalSearchToggleVisible) {
          setProgressIndex(progressIndex + 1);
        } else {
          await finalizeInitialConfig();
        }
      } else if (progressIndex === 2) {
        await finalizeInitialConfig();
      } else if (
        progressIndex === 0 &&
        draftUrl &&
        ((draftUser && draftPass) || draftApiKey)
      ) {
        await dispatch(getElserSupport({ skillId, assistantId }));
        // If user is changing credentials from the instance tab
        if (isElasticSetupValid && draft.isDirty && isElasticConfigValid) {
          await updateSkillAndEnvironment();
        }
      } else {
        // If there was no managed index previously create one
        if (
          isDocumentUploadEnabled &&
          elasticInputMethod === ELASTIC_SEARCH_INDEX_INPUT_METHODS.UPLOAD &&
          elasticState &&
          (!elasticState.hasOwnProperty('managed_index') ||
            elasticState.managed_index === '')
        ) {
          // Send API request to create upload index only if one doesn't exist.
          let createIndexResponse = null;
          createIndexResponse = await dispatch(
            createElser({ skillId, assistantId, index: indexSelected })
          );

          // We should stop the update if creating the elser index fails.
          if (createIndexResponse.payload?.index) {
            // Update the skill with the settings once the response from creating the Elser is returned
            await updateSkillAndEnvironment();
          }
        } else {
          await updateSkillAndEnvironment();
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    assistantId,
    dispatch,
    draftSkill,
    elasticInputMethod,
    indexSelected,
    environmentId,
    handleClose,
    isEnabled,
    showDetailConfig,
    skillId,
    skillReferences,
    progressIndex
  ]);

  let submitButtonLabel;
  if (
    isElasticSetupValid ||
    (progressIndex === 2 && conversationalSearchToggleVisible) ||
    (progressIndex === 1 && !conversationalSearchToggleVisible)
  ) {
    submitButtonLabel = formatMsg(messages.save);
  } else if (progressIndex === 0 || !showDetailConfig || progressIndex === 1) {
    submitButtonLabel = formatMsg(messages.next);
  } else {
    submitButtonLabel = formatMsg(messages.finish);
  }

  const isReadOnly = useAppSelector(state => isSkillReadOnly(state, skillId));

  const isSubmitButtonEnabled =
    (!isReadOnly || isCpd) &&
    ((progressIndex === 0 &&
      draftUrl &&
      ((draftUser && draftPass) || draftApiKey)) ||
      (elasticInputMethod !== null &&
        indexSelected !== '' &&
        progressIndex === 1) ||
      progressIndex === 2 ||
      (isElasticSetupValid &&
        draft.isDirty &&
        isElasticConfigValid &&
        indexSelected !== ''));

  const showLoading =
    isUpdating ||
    isEnvironmentUpdating ||
    isCreating ||
    areSkillsLoading ||
    elserSupportedLoading ||
    createElserLoading ||
    isInitializingDraftSkill;

  return (
    <Panel className="ElasticSearchOverlay" title={formatMsg(messages.title)}>
      <header
        className="ElasticSearchOverlay__header"
        aria-labelledby="integration-panel-heading">
        <div className="ElasticSearchOverlay__header--name">
          <h2 id="integration-panel-heading">{formatMsg(messages.title)}</h2>
        </div>
        <div className="ChannelOverlay__header--tag">
          <EnvironmentTag environmentId={environmentId} />
        </div>
        <div className="ElasticSearchOverlay__header--actions">
          <Button kind="secondary" onClick={handleClose} id="closePanel">
            {formatMsg(messages.close)}
          </Button>
          <Button
            kind="primary"
            onClick={handleSubmit}
            disabled={!isSubmitButtonEnabled}>
            {submitButtonLabel}
          </Button>
        </div>
      </header>
      {!isCpd && <ReadOnlyNotification readOnly={isReadOnly} />}
      <div className="ElasticSearchOverlay__content">
        {showLoading && <Loading withOverlay />}
        {isElasticSetupValid ? (
          <ElasticSearchTabs
            assistantName={assistant.name}
            draft={draft}
            skillId={skillId}
            assistantId={assistantId}
            readOnly={isReadOnly}
            elasticState={elasticState}
            onClose={onClose}
          />
        ) : (
          <>
            {conversationalSearchToggleVisible ? (
              <ProgressIndicator
                className="ElasticSearchOverlay__progress-indicator"
                currentIndex={progressIndex}
                spaceEqually={true}>
                <ProgressStep label={formatMsg(messages.connect)} />
                <ProgressStep label={formatMsg(messages.index)} />
                <ProgressStep label={formatMsg(messages.search)} />
              </ProgressIndicator>
            ) : (
              <ProgressIndicator
                className="ElasticSearchOverlay__progress-indicator"
                currentIndex={progressIndex}
                spaceEqually={true}>
                <ProgressStep label={formatMsg(messages.connect)} />
                <ProgressStep label={formatMsg(messages.index)} />
              </ProgressIndicator>
            )}
            {(elserSupportedLoading || createElserLoading) && (
              <Loading withOverlay />
            )}
            {progressIndex === 0 && (
              <ElasticSearchInstanceConfig
                readOnly={isReadOnly}
                onClose={onClose}
              />
            )}
            {progressIndex === 1 && showDetailConfig && (
              <>
                <ElasticSearchIndexConfig
                  readOnly={isReadOnly}
                  assistantName={assistant.name}
                />
              </>
            )}
            {conversationalSearchToggleVisible && progressIndex === 2 && (
              <ConversationalSearch
                searchType={SEARCH_INTEGRATION_TYPES.ELASTIC}
              />
            )}
          </>
        )}
      </div>
    </Panel>
  );
};

export { ElasticSearchOverlay };
